import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Collapse,
  TablePagination,
  InputAdornment,
  Checkbox,
} from '@mui/material';
import { Add, Edit, Delete, FilterList, Clear, Search, CheckCircle, Schedule, Email, Refresh } from '@mui/icons-material';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { transactionsAPI, categoriesAPI, banksAPI, Bank, transactionApprovalsAPI, failedEmailJobsAPI } from '../services/api';
import TransactionApprovalsPage from './TransactionApprovalsPage';
import PlannedExpensesPage from './PlannedExpensesPage';

interface Transaction {
  id: number;
  amount: number;
  description: string;
  date: string;
  transaction_type: 'income' | 'expense';
  category: { id: number; name: string; color: string };
  bank: { id: number; name: string };
  to_bank?: { id: number; name: string };
  to_bank_name?: string;
  reference_number?: string;
}

interface Category {
  id: number;
  name: string;
  color: string;
}



interface PaginationInfo {
  current_page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
}

interface TransactionSummary {
  total_income: number;
  total_expense: number;
  net_amount: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`transactions-tabpanel-${index}`}
      aria-labelledby={`transactions-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const TransactionsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    current_page: 1,
    per_page: 20,
    total_count: 0,
    total_pages: 0,
  });
  const [summary, setSummary] = useState<TransactionSummary>({
    total_income: 0,
    total_expense: 0,
    net_amount: 0,
  });
  
  // Filter states
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [filters, setFilters] = useState({
    type: '',
    category_id: '',
    bank_id: '',
    start_date: '',
    end_date: '',
    min_amount: '',
    max_amount: '',
    search: '',
    sort_by: 'date',
    sort_order: 'desc' as 'asc' | 'desc',
  });

  const [formData, setFormData] = useState({
    amount: '',
    description: '',
    date: new Date().toISOString().slice(0, 16),
    transaction_type: 'expense' as 'income' | 'expense',
    category_id: '',
    bank_id: '',
    to_bank_id: '',
    to_bank_name: '',
    is_outside: false,
    is_transfer: false,
    reference_number: '',
  });

  useEffect(() => {
    fetchData();
  }, [pagination.current_page, pagination.per_page, filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Convert filter values to proper types for API
      const apiFilters = {
        type: filters.type || undefined,
        category_id: filters.category_id ? parseInt(filters.category_id) : undefined,
        bank_id: filters.bank_id ? parseInt(filters.bank_id) : undefined,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        min_amount: filters.min_amount ? parseFloat(filters.min_amount) : undefined,
        max_amount: filters.max_amount ? parseFloat(filters.max_amount) : undefined,
        search: filters.search || undefined,
        sort_by: filters.sort_by,
        sort_order: filters.sort_order,
        approved: true, // Only show approved transactions
      };
      
      const [transactionsRes, categoriesRes, banksRes] = await Promise.all([
        transactionsAPI.getAll({
          ...apiFilters,
          page: pagination.current_page,
          per_page: pagination.per_page,
        }),
        categoriesAPI.getAll(),
        banksAPI.getAll(),
      ]);
      
      setTransactions(transactionsRes.data.transactions);
      setPagination(transactionsRes.data.pagination);
      setSummary(transactionsRes.data.summary || {
        total_income: 0,
        total_expense: 0,
        net_amount: 0,
      });
      // Handle new API response format for categories
      setCategories(categoriesRes.data.data || categoriesRes.data || []);
      // Handle new API response format for banks
      setBanks(banksRes.data.data || banksRes.data || []);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingTransaction(null);
    setFormData({
      amount: '',
      description: '',
      date: new Date().toISOString().slice(0, 16),
      transaction_type: 'expense',
      category_id: '',
      bank_id: '',
      to_bank_id: '',
      to_bank_name: '',
      is_outside: false,
      is_transfer: false,
      reference_number: '',
    });
    setDialogOpen(true);
  };

  const handleEdit = (transaction: Transaction) => {
    setEditingTransaction(transaction);
    
    // Format date for datetime-local input (YYYY-MM-DDTHH:mm) - preserve local timezone
    const formatDateForInput = (dateString: string) => {
      const date = new Date(dateString);
      // Get local date and time without timezone conversion
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };
    
    // Debug: Log the transaction data being set
    console.log('Editing transaction:', transaction);
    console.log('Transaction type from data:', transaction.transaction_type);
    
    const newFormData = {
      amount: transaction.amount.toString(),
      description: transaction.description,
      date: formatDateForInput(transaction.date),
      transaction_type: transaction.transaction_type as 'income' | 'expense',
      category_id: transaction.category.id.toString(),
      bank_id: transaction.bank.id.toString(),
      to_bank_id: transaction.to_bank?.id?.toString() || '',
      to_bank_name: transaction.to_bank_name || '',
      is_outside: !transaction.to_bank && !!transaction.to_bank_name,
      is_transfer: !!(transaction.to_bank || transaction.to_bank_name),
      reference_number: transaction.reference_number || '',
    };
    
    console.log('New form data:', newFormData);
    setFormData(newFormData);
    setDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this transaction?')) {
      try {
        await transactionsAPI.delete(id);
        fetchData();
      } catch (error: any) {
        setError(error.response?.data?.error || 'Failed to delete transaction');
      }
    }
  };

  const handleSubmit = async () => {
    try {
      // Debug: Log the form data being submitted
      console.log('Submitting form data:', formData);
      console.log('Transaction type in form:', formData.transaction_type);
      
      // Handle transfer logic
              if (formData.is_transfer && formData.bank_id && formData.to_bank_id && !formData.is_outside) {
        // Create two transactions for bank-to-bank transfer
        const baseData = {
          amount: parseFloat(formData.amount),
          date: formData.date,
          category_id: parseInt(formData.category_id),
        };

        // Outgoing transaction from source bank
        const outgoingData = {
          ...baseData,
          description: `${formData.description} (Transfer Out)`,
          transaction_type: 'expense' as const,
          bank_id: parseInt(formData.bank_id),
          to_bank_id: parseInt(formData.to_bank_id),
          reference_number: formData.reference_number || undefined,
        };

        // Incoming transaction to destination bank
        const incomingData = {
          ...baseData,
          description: `${formData.description} (Transfer In)`,
          transaction_type: 'income' as const,
          bank_id: parseInt(formData.to_bank_id),
          to_bank_id: parseInt(formData.bank_id),
          reference_number: formData.reference_number || undefined,
        };

        if (editingTransaction) {
          // Use form values (bank_id, to_bank_id) - only description/type differ by Out vs In
          const updateData = editingTransaction.transaction_type === 'income'
            ? { ...baseData, description: `${formData.description} (Transfer In)`, transaction_type: 'income' as const, bank_id: parseInt(formData.bank_id), to_bank_id: parseInt(formData.to_bank_id), reference_number: formData.reference_number || undefined }
            : outgoingData;
          await transactionsAPI.update(editingTransaction.id, updateData);
        } else {
          // Create both transactions
          await Promise.all([
            transactionsAPI.create(outgoingData),
            transactionsAPI.create(incomingData),
          ]);
        }
      } else {
        // Regular transaction (non-transfer or external transfer)
        const data: any = {
          amount: parseFloat(formData.amount),
          description: formData.description,
          date: formData.date,
          transaction_type: formData.transaction_type,
          category_id: parseInt(formData.category_id),
          bank_id: parseInt(formData.bank_id),
          reference_number: formData.reference_number || undefined,
        };
        
        // Debug: Log the data being sent to backend
        console.log('Data being sent to backend:', data);
        console.log('Transaction type being sent:', data.transaction_type);

        // Handle transfer fields - only include if it's actually a transfer
        if (formData.is_transfer) {
          if (formData.is_outside) {
            data.to_bank_name = formData.to_bank_name;
          } else {
            data.to_bank_id = parseInt(formData.to_bank_id);
          }
        } else {
          // Clear transfer fields for non-transfer transactions
          data.to_bank_id = null;
          data.to_bank_name = null;
        }

        if (editingTransaction) {
          await transactionsAPI.update(editingTransaction.id, data);
        } else {
          await transactionsAPI.create(data);
        }
      }

      setDialogOpen(false);
      fetchData();
    } catch (error: any) {
      setError(error.response?.data?.errors?.join(', ') || 'Failed to save transaction');
    }
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, current_page: 1 })); // Reset to first page
  };

  const clearFilters = () => {
    setFilters({
      type: '',
      category_id: '',
      bank_id: '',
      start_date: '',
      end_date: '',
      min_amount: '',
      max_amount: '',
      search: '',
      sort_by: 'date',
      sort_order: 'desc',
    });
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPagination(prev => ({ ...prev, current_page: newPage + 1 }));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPagination(prev => ({ 
      ...prev, 
      per_page: parseInt(event.target.value, 10),
      current_page: 1 
    }));
  };

  const getTransactionTypeColor = (type: string) => {
    switch (type) {
      case 'income':
        return 'success';
      case 'expense':
        return 'error';
      default:
        return 'default';
    }
  };

  const getTransactionTypeIcon = (type: string) => {
    switch (type) {
      case 'income':
        return '+';
      case 'expense':
        return '-';
      default:
        return '';
    }
  };

  const getTransferDestination = (transaction: Transaction) => {
    if (transaction.to_bank) {
      return transaction.to_bank.name;
    } else if (transaction.to_bank_name) {
      return transaction.to_bank_name;
    }
    return '';
  };

  const formatCurrency = (value: any): string => {
    const num = typeof value === 'string' ? parseFloat(value) : Number(value);
    if (isNaN(num)) return '₹0.00';
    
    // Use JavaScript's built-in Indian number formatting
    return `₹${num.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const getTransferSuggestion = (fromBankId: string, toBankId: string) => {
    if (!fromBankId || !toBankId || fromBankId === toBankId) {
      return null;
    }
    
    const fromBank = banks.find(b => b.id.toString() === fromBankId);
    const toBank = banks.find(b => b.id.toString() === toBankId);
    
    if (fromBank && toBank) {
      return {
        message: `Transfer from ${fromBank.name} to ${toBank.name}`,
        type: 'info' as const
      };
    }
    
    return null;
  };

  if (loading && transactions.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Transactions</Typography>
        {activeTab === 0 && (
          <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
            Add Transaction
          </Button>
        )}
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Transactions" icon={<Search />} iconPosition="start" />
          <Tab label="Approvals" icon={<CheckCircle />} iconPosition="start" />
          <Tab label="Planned Expenses" icon={<Schedule />} iconPosition="start" />
          <Tab label="Email Failed Jobs" icon={<Email />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <TabPanel value={activeTab} index={0}>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filters Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h6">
                Filters ({pagination.total_count} transactions)
              </Typography>
              {pagination.total_count > 0 && (
                <Box sx={{ mt: 0.5, display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Income: <span style={{ color: '#4caf50', fontWeight: 'bold' }}>{formatCurrency(summary.total_income)}</span>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Expense: <span style={{ color: '#f44336', fontWeight: 'bold' }}>{formatCurrency(summary.total_expense)}</span>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Net: <span style={{ 
                      color: summary.net_amount >= 0 ? '#4caf50' : '#f44336', 
                      fontWeight: 'bold' 
                    }}>{formatCurrency(summary.net_amount)}</span>
                  </Typography>
                </Box>
              )}
            </Box>
            <Box>
              <Button
                startIcon={<FilterList />}
                onClick={() => setFiltersExpanded(!filtersExpanded)}
                size="small"
              >
                {filtersExpanded ? 'Hide' : 'Show'} Filters
              </Button>
              <Button
                startIcon={<Clear />}
                onClick={clearFilters}
                size="small"
                sx={{ ml: 1 }}
              >
                Clear
              </Button>
            </Box>
          </Box>

          <Collapse in={filtersExpanded}>
            <Grid container spacing={2}>
              {/* Search */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Search Description"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Transaction Type */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={filters.type}
                    label="Type"
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="expense">Expense</MenuItem>
                    <MenuItem value="income">Income</MenuItem>

                  </Select>
                </FormControl>
              </Grid>

              {/* Category */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={filters.category_id}
                    label="Category"
                    onChange={(e) => handleFilterChange('category_id', e.target.value)}
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Bank */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Bank</InputLabel>
                  <Select
                    value={filters.bank_id}
                    label="Bank"
                    onChange={(e) => handleFilterChange('bank_id', e.target.value)}
                  >
                    <MenuItem value="">All Banks</MenuItem>
                    {banks.map((bank) => (
                      <MenuItem key={bank.id} value={bank.id}>
                        {bank.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Date Range */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange('start_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange('end_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Amount Range */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Min Amount"
                  type="number"
                  value={filters.min_amount}
                  onChange={(e) => handleFilterChange('min_amount', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Max Amount"
                  type="number"
                  value={filters.max_amount}
                  onChange={(e) => handleFilterChange('max_amount', e.target.value)}
                />
              </Grid>

              {/* Sorting */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={filters.sort_by}
                    label="Sort By"
                    onChange={(e) => handleFilterChange('sort_by', e.target.value)}
                  >
                    <MenuItem value="date">Date</MenuItem>
                    <MenuItem value="amount">Amount</MenuItem>
                    <MenuItem value="description">Description</MenuItem>
                    <MenuItem value="transaction_type">Type</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Sort Order</InputLabel>
                  <Select
                    value={filters.sort_order}
                    label="Sort Order"
                    onChange={(e) => handleFilterChange('sort_order', e.target.value)}
                  >
                    <MenuItem value="desc">Descending</MenuItem>
                    <MenuItem value="asc">Ascending</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Collapse>
        </CardContent>
      </Card>



      {/* Transactions Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Bank</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Reference</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((transaction) => (
              <TableRow key={transaction.id}>
                <TableCell>{new Date(transaction.date).toLocaleString()}</TableCell>
                <TableCell>
                  {transaction.description}
                  {(transaction.to_bank || transaction.to_bank_name) && (
                    <Typography variant="caption" display="block" color="textSecondary">
                      To: {getTransferDestination(transaction)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={transaction.category.name}
                    size="small"
                    sx={{ backgroundColor: transaction.category.color, color: 'white' }}
                  />
                </TableCell>
                <TableCell>{transaction.bank.name}</TableCell>
                <TableCell>
                  <Chip
                    label={transaction.transaction_type}
                    size="small"
                    color={getTransactionTypeColor(transaction.transaction_type) as 'success' | 'error' | 'info' | 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  <Typography
                    color={
                      transaction.transaction_type === 'income' 
                        ? 'success.main' 
                        : transaction.transaction_type === 'expense'
                        ? 'error.main'
                        : 'info.main'
                    }
                  >
                    {getTransactionTypeIcon(transaction.transaction_type) + formatCurrency(transaction.amount)}
                  </Typography>
                </TableCell>
                <TableCell>
                  {transaction.reference_number ? (
                    <Typography variant="caption" color="textSecondary">
                      {transaction.reference_number}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="textSecondary">
                      -
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleEdit(transaction)}>
                    <Edit />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(transaction.id)}>
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {transactions.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="textSecondary">No transactions found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={pagination.total_count}
        page={pagination.current_page - 1}
        onPageChange={handlePageChange}
        rowsPerPage={pagination.per_page}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[10, 20, 50, 100]}
        labelDisplayedRows={({ from, to, count }) => 
          `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
        }
      />

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTransaction ? 'Edit Transaction' : 'Add Transaction'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* First Row: Bank */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Bank</InputLabel>
                <Select
                  value={formData.bank_id}
                  label="Bank"
                  onChange={(e) => setFormData({ ...formData, bank_id: e.target.value })}
                >
                  {banks.map((bank) => (
                    <MenuItem key={bank.id} value={bank.id}>
                      {bank.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Second Row: Amount */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              />
            </Grid>

            {/* Third Row: Type and Date */}
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.transaction_type}
                  label="Type"
                  onChange={(e) => {
                    console.log('Type changed to:', e.target.value);
                    setFormData({ ...formData, transaction_type: e.target.value as 'income' | 'expense' });
                  }}
                >
                  <MenuItem value="expense">Expense</MenuItem>
                  <MenuItem value="income">Income</MenuItem>
                </Select>
              </FormControl>
              {/* Debug: Show current form value */}
              <Typography variant="caption" color="textSecondary">
                Debug: Current form type = {formData.transaction_type}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Date"
                type="datetime-local"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            {/* Transfer Checkbox */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_transfer}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      if (e.target.checked) {
                        setFormData({ ...formData, is_transfer: true });
                      } else {
                        // Clear transfer fields when unchecking
                        setFormData({ 
                          ...formData, 
                          is_transfer: false, 
                          to_bank_id: '', 
                          to_bank_name: '',
                          is_outside: false 
                        });
                      }
                    }}
                  />
                }
                label="This is a transfer transaction"
              />
            </Grid>

            {/* Transfer-specific fields */}
            {formData.is_transfer && (
              <>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Transfer To</InputLabel>
                    <Select
                      value={formData.is_outside ? 'outside' : formData.to_bank_id}
                      label="Transfer To"
                      onChange={(e) => {
                        if (e.target.value === 'outside') {
                          setFormData({ ...formData, is_outside: true, to_bank_id: '', to_bank_name: '' });
                        } else {
                          setFormData({ ...formData, is_outside: false, to_bank_id: e.target.value, to_bank_name: '' });
                        }
                      }}
                    >
                      <MenuItem value="outside">🌍 Outside</MenuItem>
                      {banks.map((bank) => (
                        <MenuItem key={bank.id} value={bank.id}>
                          {bank.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Transfer Suggestion */}
                {formData.is_transfer && formData.bank_id && formData.to_bank_id && !formData.is_outside && (
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mt: 1 }}>
                      💡 <strong>Transfer Logic:</strong> This will create two transactions:
                      <br />• <strong>Outgoing:</strong> From {banks.find(b => b.id.toString() === formData.bank_id)?.name} (Expense)
                      <br />• <strong>Incoming:</strong> To {banks.find(b => b.id.toString() === formData.to_bank_id)?.name} (Income)
                    </Alert>
                  </Grid>
                )}
                
                {formData.is_outside && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Outside Location"
                      placeholder="e.g., PayPal, Venmo, etc."
                      value={formData.to_bank_name}
                      onChange={(e) => setFormData({ ...formData, to_bank_name: e.target.value })}
                    />
                  </Grid>
                )}
              </>
            )}

            {/* Fourth Row: Category and Description */}
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category_id}
                  label="Category"
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            
            {/* Fifth Row: Reference Number */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reference Number (Optional)"
                placeholder="e.g., UTR123456, Cheque No, Transaction ID"
                value={formData.reference_number}
                onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                helperText="Bank reference number, UTR, cheque number, etc."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingTransaction ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <TransactionApprovalsPage />
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <PlannedExpensesPage />
      </TabPanel>

      <TabPanel value={activeTab} index={3}>
        <FailedEmailJobsTab />
      </TabPanel>
    </Box>
  );
};

// Failed Email Jobs Tab Component
const FailedEmailJobsTab: React.FC = () => {
  const [failedJobs, setFailedJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [retryingIds, setRetryingIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    loadFailedJobs();
  }, []);

  const loadFailedJobs = async () => {
    try {
      setLoading(true);
      const response = await failedEmailJobsAPI.getAll();
      setFailedJobs(response.data.failed_jobs || []);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load failed email jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (id: number) => {
    try {
      setRetryingIds(prev => new Set(prev).add(id));
      await failedEmailJobsAPI.retry(id);
      setSuccess('Job retried successfully');
      setTimeout(() => setSuccess(null), 3000);
      loadFailedJobs();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to retry job');
    } finally {
      setRetryingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const handleBulkRetry = async () => {
    const selectedJobs = failedJobs.filter(job => job.status === 'failed');
    if (selectedJobs.length === 0) {
      setError('No failed jobs to retry');
      return;
    }

    if (!window.confirm(`Retry ${selectedJobs.length} failed job(s)?`)) {
      return;
    }

    try {
      const jobIds = selectedJobs.map(job => job.id);
      setRetryingIds(new Set(jobIds));
      await failedEmailJobsAPI.bulkRetry(jobIds);
      setSuccess(`${selectedJobs.length} job(s) retried successfully`);
      setTimeout(() => setSuccess(null), 3000);
      loadFailedJobs();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to retry jobs');
    } finally {
      setRetryingIds(new Set());
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Failed Email Processing Jobs</Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadFailedJobs}
            size="small"
          >
            Refresh
          </Button>
          {failedJobs.length > 0 && (
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={handleBulkRetry}
              size="small"
              color="primary"
            >
              Retry All ({failedJobs.length})
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {failedJobs.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Email sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Failed Email Jobs
          </Typography>
          <Typography color="textSecondary">
            All email processing jobs have completed successfully.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Failed At</TableCell>
                <TableCell>Error Message</TableCell>
                <TableCell>Email Preview</TableCell>
                <TableCell>Retry Count</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {failedJobs.map((job) => (
                <TableRow key={job.id}>
                  <TableCell>
                    {new Date(job.failed_at || job.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="error">
                      {job.error_message || 'Unknown error'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {job.email_body?.substring(0, 100)}...
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={job.retry_count || 0} size="small" />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Refresh />}
                      onClick={() => handleRetry(job.id)}
                      disabled={retryingIds.has(job.id)}
                    >
                      {retryingIds.has(job.id) ? 'Retrying...' : 'Retry'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default TransactionsPage;
